/**
 * Hook for managing URL search parameters
 *
 * Provides utilities for reading and updating URL search params
 * without causing page navigation
 */

import { useCallback, useEffect, useMemo, useState } from "react"

export interface UrlParams {
  [key: string]: string | undefined
}

/**
 * Get current URL search params as an object
 */
export function getUrlParams(): UrlParams {
  const searchParams = new URLSearchParams(window.location.search)
  const params: UrlParams = {}
  for (const [key, value] of searchParams.entries()) {
    params[key] = value
  }
  return params
}

/**
 * Update URL search params without navigation
 */
export function updateUrlParams(params: UrlParams): void {
  const searchParams = new URLSearchParams(window.location.search)

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      searchParams.delete(key)
    } else {
      searchParams.set(key, value)
    }
  }

  const newSearch = searchParams.toString()
  const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname

  // Use replaceState to update URL without adding to history
  window.history.replaceState({}, "", newUrl)

  // Dispatch a custom event to notify listeners
  window.dispatchEvent(new CustomEvent("urlparamschange"))
}

/**
 * Hook to sync a single URL param with state
 */
export function useUrlParam<T extends string>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const params = getUrlParams()
    return (params[key] as T) || defaultValue
  })

  // Listen for popstate events (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const params = getUrlParams()
      setValue((params[key] as T) || defaultValue)
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [key, defaultValue])

  const setParamValue = useCallback(
    (newValue: T) => {
      setValue(newValue)
      updateUrlParams({ [key]: newValue === defaultValue ? undefined : newValue })
    },
    [key, defaultValue],
  )

  return [value, setParamValue]
}

/**
 * Hook to manage multiple URL params at once
 */
export function useUrlParams<T extends UrlParams>(
  defaultParams: T,
): [T, (params: Partial<T>) => void] {
  // Memoize the default params keys array to avoid unnecessary re-renders
  const defaultParamsKeys = useMemo(() => Object.keys(defaultParams), [defaultParams])

  const [params, setParams] = useState<T>(() => {
    const urlParams = getUrlParams()
    const result = { ...defaultParams }
    for (const key of defaultParamsKeys) {
      if (urlParams[key] !== undefined) {
        ;(result as UrlParams)[key] = urlParams[key]
      }
    }
    return result
  })

  // Listen for popstate events (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = getUrlParams()
      const result = { ...defaultParams }
      for (const key of defaultParamsKeys) {
        if (urlParams[key] !== undefined) {
          ;(result as UrlParams)[key] = urlParams[key]
        }
      }
      setParams(result)
    }

    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [defaultParams, defaultParamsKeys])

  const updateParams = useCallback(
    (newParams: Partial<T>) => {
      setParams((prev) => {
        const updated = { ...prev, ...newParams }
        // Update URL with changed values
        const urlUpdates: UrlParams = {}
        for (const [key, value] of Object.entries(newParams)) {
          const stringValue = value !== undefined && value !== null ? String(value) : undefined
          urlUpdates[key] = stringValue === defaultParams[key] ? undefined : stringValue
        }
        updateUrlParams(urlUpdates)
        return updated
      })
    },
    [defaultParams],
  )

  return [params, updateParams]
}
